import { buildGs1DigitalLinkUrl, GS1_PRIMARY_KEY_AIS, type PrimaryKeyAI } from '@myplayer/gs1';
import {
  IdentifierDraftSchema,
  SECTOR_TEMPLATES,
  TargetLinkSchema,
  type DraftTargetLink,
  type IdentifierDraft,
  type IdentifierRecord,
  type SectorId,
} from '@myplayer/schema';
import { validateIdentifierCandidate } from '@myplayer/validation';
import Papa from 'papaparse';
import { getFirebaseClients } from './firebase.js';
import {
  checkAdminIdentifierUniqueness,
  createStagedIdentifierInFirestore,
  formatPlainValidationError,
  resolveIdentifierLifecycleState,
  validateGs1KeyInput,
} from './products-firestore.js';
import { resolveEffectiveOrigin } from './use-workspace-identifiers.js';

export interface ValidatedCsvRow {
  readonly rowNumber: number;
  readonly ai: PrimaryKeyAI;
  readonly primaryKey: string;
  readonly identifierKey: string;
  readonly title: string;
  readonly brand?: string;
  readonly sector: SectorId;
  readonly defaultUrl: string;
  readonly links: readonly DraftTargetLink[];
  readonly draft: IdentifierDraft;
}

export interface InvalidCsvRow {
  readonly rowNumber: number;
  readonly rawAi: string;
  readonly rawPrimaryKey: string;
  readonly rawTitle: string;
  readonly rawDefaultUrl: string;
  readonly reasons: readonly string[];
}

export interface CsvImportValidationReport {
  readonly totalRows: number;
  readonly validRows: readonly ValidatedCsvRow[];
  readonly invalidRows: readonly InvalidCsvRow[];
}

function isPrimaryKeyAi(value: string): value is PrimaryKeyAI {
  return (GS1_PRIMARY_KEY_AIS as readonly string[]).includes(value);
}

const ALLOWED_SECTOR_IDS = Object.keys(SECTOR_TEMPLATES) as readonly SectorId[];

function isSectorId(value: string): value is SectorId {
  return (ALLOWED_SECTOR_IDS as readonly string[]).includes(value);
}

function normalizeHeaderKey(header: string): string {
  return header
    .trim()
    .replace(/[\s_-]+/g, '')
    .toLowerCase();
}

function getRowField(row: Readonly<Record<string, string>>, candidates: readonly string[]): string {
  const normalizedCandidates = candidates.map((c) => normalizeHeaderKey(c));
  for (const [key, val] of Object.entries(row)) {
    if (normalizedCandidates.includes(normalizeHeaderKey(key)) && typeof val === 'string') {
      return val.trim();
    }
  }
  return '';
}

/**
 * Parses and validates a CSV string of GS1 identifiers using `papaparse`, `@myplayer/gs1`,
 * `@myplayer/schema` (Zod), and `@myplayer/validation`.
 * Returns a full report of valid rows (ready to stage as drafts) and invalid rows with plain-language reasons.
 */
export function parseAndValidateIdentifiersCsv(
  csvText: string,
  options: {
    readonly submittedBy?: string;
    readonly resolverHosts?: readonly string[];
    readonly existingRecords?: readonly IdentifierRecord[];
    readonly orgId?: string;
    readonly workspaceId?: string;
    readonly workspaceNameMap?: ReadonlyMap<string, string>;
  } = {},
): CsvImportValidationReport {
  const trimmedText = csvText.trim();
  if (trimmedText.length === 0) {
    return { totalRows: 0, validRows: [], invalidRows: [] };
  }

  const parsed = Papa.parse<Record<string, string>>(trimmedText, {
    header: true,
    skipEmptyLines: 'greedy',
  });

  const validRows: ValidatedCsvRow[] = [];
  const invalidRows: InvalidCsvRow[] = [];
  const seenKeysInCsv = new Map<string, number>();
  const submittedBy = options.submittedBy ?? 'uid_admin';
  const nowIso = new Date().toISOString();

  parsed.data.forEach((rawRow, idx) => {
    const rowNumber = idx + 2; // 1-based row number accounting for header row
    const rawAiInput = getRowField(rawRow, ['ai', 'gs1ai', 'applicationidentifier']) || '01';
    const rawAi = rawAiInput.padStart(2, '0');
    const rawPrimaryKey = getRowField(rawRow, [
      'primaryKey',
      'gtin',
      'key',
      'barcode',
      'identifier',
    ]);
    const rawTitle = getRowField(rawRow, ['title', 'productTitle', 'name']);
    const rawBrand = getRowField(rawRow, ['brand', 'brandName']);
    const rawSector = getRowField(rawRow, ['sector']) || 'RETAIL_CPG';
    const rawDefaultUrl = getRowField(rawRow, ['defaultUrl', 'url', 'href', 'targetUrl']);
    const rawLinkTitle = getRowField(rawRow, ['linkTitle']) || rawTitle || 'Product Information';
    const rawLinkType = getRowField(rawRow, ['linkType']) || 'gs1:pip';
    const rawHreflang = getRowField(rawRow, ['hreflang', 'language']) || 'en';
    const rawContext = getRowField(rawRow, ['context', 'region']) || 'GLOBAL';

    const reasons: string[] = [];

    if (!isPrimaryKeyAi(rawAi)) {
      reasons.push(
        `Unsupported GS1 Application Identifier "${rawAiInput}". Use a supported primary key AI such as 01 (GTIN).`,
      );
    }

    const aiForCheck: PrimaryKeyAI = isPrimaryKeyAi(rawAi) ? rawAi : '01';
    const gs1Result = validateGs1KeyInput(aiForCheck, rawPrimaryKey);
    if (!gs1Result.ok) {
      reasons.push(
        formatPlainValidationError({
          code: gs1Result.errorCode,
          message: gs1Result.message,
        }),
      );
    }

    if (rawTitle.length === 0) {
      reasons.push('Product title is required.');
    }

    if (!isSectorId(rawSector)) {
      reasons.push(
        `Unsupported sector "${rawSector}". Allowed sectors: ${ALLOWED_SECTOR_IDS.join(', ')}.`,
      );
    }

    const hreflangList = rawHreflang
      .split(/[,;|]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const contextList = rawContext
      .split(/[,;|]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const candidateLink: DraftTargetLink = {
      linkType: rawLinkType,
      href: rawDefaultUrl,
      title: rawLinkTitle,
      type: 'text/html',
      hreflang: hreflangList.length > 0 ? hreflangList : ['en'],
      context: contextList.length > 0 ? contextList : ['GLOBAL'],
      defaultLink: true,
    };

    const linkZod = TargetLinkSchema.safeParse(candidateLink);
    if (!linkZod.success) {
      for (const issue of linkZod.error.issues) {
        reasons.push(
          formatPlainValidationError({
            code: issue.code,
            message: issue.message,
          }),
        );
      }
    }

    const draftCandidate: IdentifierDraft = {
      title: rawTitle,
      ...(rawBrand.length > 0 ? { brand: rawBrand } : {}),
      status: 'ACTIVE',
      defaultUrl: rawDefaultUrl,
      links: [candidateLink],
      rules: [],
      submittedBy,
      submittedAt: nowIso,
    };

    const draftZod = IdentifierDraftSchema.safeParse(draftCandidate);
    if (!draftZod.success) {
      for (const issue of draftZod.error.issues) {
        const plain = formatPlainValidationError({
          code: issue.code,
          message: issue.message,
        });
        if (!reasons.includes(plain)) {
          reasons.push(plain);
        }
      }
    }

    if (gs1Result.ok) {
      const candidateValidation = validateIdentifierCandidate(
        gs1Result.ai,
        gs1Result.canonicalPrimaryKey,
        rawDefaultUrl,
        [candidateLink],
        {
          requireSingleDefaultLink: true,
          ...(options.resolverHosts !== undefined ? { resolverHosts: options.resolverHosts } : {}),
        },
      );
      if (candidateValidation.status === 'INVALID') {
        for (const err of candidateValidation.errors) {
          const plain = formatPlainValidationError(err);
          if (!reasons.includes(plain)) {
            reasons.push(plain);
          }
        }
      }

      const firstSeenRow = seenKeysInCsv.get(gs1Result.identifierKey);
      if (firstSeenRow !== undefined) {
        reasons.push(`Duplicate GTIN in CSV import (already listed on row #${firstSeenRow}).`);
      } else {
        seenKeysInCsv.set(gs1Result.identifierKey, rowNumber);
      }

      if (options.existingRecords !== undefined) {
        const conflictReason = checkAdminIdentifierUniqueness({
          identifierKey: gs1Result.identifierKey,
          orgId: options.orgId ?? 'org_default',
          workspaceId: options.workspaceId ?? 'ws_default',
          existingRecords: options.existingRecords,
          ...(options.workspaceNameMap !== undefined
            ? { workspaceNameMap: options.workspaceNameMap }
            : {}),
        });
        if (conflictReason !== null && !reasons.includes(conflictReason)) {
          reasons.push(conflictReason);
        }
      }
    }

    if (reasons.length > 0 || !gs1Result.ok || !isSectorId(rawSector)) {
      invalidRows.push({
        rowNumber,
        rawAi: rawAiInput,
        rawPrimaryKey,
        rawTitle,
        rawDefaultUrl,
        reasons: reasons.length > 0 ? reasons : ['Row failed validation.'],
      });
      return;
    }

    validRows.push({
      rowNumber,
      ai: gs1Result.ai,
      primaryKey: gs1Result.canonicalPrimaryKey,
      identifierKey: gs1Result.identifierKey,
      title: rawTitle,
      ...(rawBrand.length > 0 ? { brand: rawBrand } : {}),
      sector: rawSector,
      defaultUrl: rawDefaultUrl,
      links: [candidateLink],
      draft: draftCandidate,
    });
  });

  return {
    totalRows: parsed.data.length,
    validRows,
    invalidRows,
  };
}

/**
 * Stages validated CSV rows as drafts ONLY (`draft` sub-object).
 * Never writes live fields (`defaultUrl`, `links`, `status: 'ACTIVE'`, `version`, `validation`).
 */
export async function stageValidatedCsvRowsAsDrafts(params: {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly uid: string;
  readonly validRows: readonly ValidatedCsvRow[];
  readonly existingRecords: readonly IdentifierRecord[];
  readonly isControlled: boolean;
  readonly knownWorkspaceIds?: readonly string[];
  readonly workspaceNameMap?: ReadonlyMap<string, string>;
}): Promise<readonly IdentifierRecord[]> {
  for (const row of params.validRows) {
    const conflict = checkAdminIdentifierUniqueness({
      identifierKey: row.identifierKey,
      orgId: params.orgId,
      workspaceId: params.workspaceId,
      existingRecords: params.existingRecords,
      ...(params.workspaceNameMap !== undefined
        ? { workspaceNameMap: params.workspaceNameMap }
        : {}),
    });
    if (conflict !== null) {
      throw new Error(conflict);
    }
  }

  if (!params.isControlled) {
    const { db } = getFirebaseClients();
    for (const row of params.validRows) {
      await createStagedIdentifierInFirestore({
        db,
        orgId: params.orgId,
        workspaceId: params.workspaceId,
        uid: params.uid,
        ai: row.ai,
        primaryKey: row.primaryKey,
        title: row.title,
        ...(row.brand !== undefined ? { brand: row.brand } : {}),
        sector: row.sector,
        defaultUrl: row.defaultUrl,
        links: row.links,
        ...(params.knownWorkspaceIds !== undefined
          ? { knownWorkspaceIds: params.knownWorkspaceIds }
          : {}),
        ...(params.workspaceNameMap !== undefined
          ? { workspaceNameMap: params.workspaceNameMap }
          : {}),
      });
    }
  }

  return params.validRows.map((row): IdentifierRecord => ({
    identifierKey: row.identifierKey,
    orgId: params.orgId,
    workspaceId: params.workspaceId,
    ai: row.ai,
    primaryKey: row.primaryKey,
    sector: row.sector,
    createdBy: params.uid,
    updatedBy: params.uid,
    draft: row.draft,
  }));
}

/**
 * Serializes workspace identifiers to CSV using `Papa.unparse`, including the canonical `liveUrl`
 * (`http(s)://${effectiveResolverHost}/${ai}/${primaryKey}`) for each identifier.
 */
export function exportIdentifiersToCsv(
  records: readonly IdentifierRecord[],
  effectiveResolverHost: string,
): string {
  const host = effectiveResolverHost.trim();
  const rows = records.map((rec) => {
    const lifecycleState = resolveIdentifierLifecycleState(rec);
    const title = rec.draft?.title ?? rec.title ?? '';
    const brand = rec.draft?.brand ?? rec.brand ?? '';
    const sector = rec.sector ?? 'RETAIL_CPG';
    const defaultUrl = rec.draft?.defaultUrl ?? rec.defaultUrl ?? '';
    let liveUrl = '';
    if (host.length > 0) {
      try {
        liveUrl = buildGs1DigitalLinkUrl({
          host,
          ai: rec.ai,
          primaryKey: rec.primaryKey,
        });
      } catch {
        const origin = resolveEffectiveOrigin(host);
        liveUrl = `${origin}/${rec.ai}/${encodeURIComponent(rec.primaryKey)}`;
      }
    }

    return {
      identifierKey: rec.identifierKey,
      ai: rec.ai,
      primaryKey: rec.primaryKey,
      title,
      brand,
      sector,
      lifecycleState,
      defaultUrl,
      liveUrl,
    };
  });

  return Papa.unparse(rows, {
    columns: [
      'identifierKey',
      'ai',
      'primaryKey',
      'title',
      'brand',
      'sector',
      'lifecycleState',
      'defaultUrl',
      'liveUrl',
    ],
  });
}
